/**
 * The Meta Pixel (Facebook and Instagram ads), so the ads can be measured and
 * aimed. public/privacy.html is broad on purpose (the owner's call, Oct 16):
 * it covers ad tools in general and promises one thing, that conversations
 * never reach advertisers. scripts/check-meta-pixel.js holds this file to the
 * rules below. $0, no network.
 *
 * What goes to Meta:
 *   - one PageView when the app opens (skipped when the visitor just came
 *     through an ad landing page under /q/, which already counted it);
 *   - "Lead", with no parameters, when someone joins the email list;
 *   - "Contact", with no parameters, when someone sends "Ask the real Rob";
 *   - "FirstQuestion" (a custom event), with no parameters, once per visit,
 *     when the visitor asks their own first question: typed, spoken or a
 *     starter, but not the question an ad asked for them, and not a "go
 *     deeper" re-ask. Haley makes it a custom conversion in Events Manager so
 *     Meta can aim the ads at people who ask, not just people who tap.
 * Nothing else. No custom data, no advanced matching, and fbq('set',
 * 'autoConfig', false) goes BEFORE init so the pixel does not scrape button
 * labels or page metadata on its own. Limited Data Use has been off since
 * Oct 15, by the owner's call: the page views and the events may build ad
 * audiences (retargeting, lookalikes).
 * Nothing a person types, no answer, no name, no email ever reaches it.
 *
 * When it does not run at all: the team's portal (a separate bundle that never
 * imports this file), Do Not Track, Global Privacy Control, localhost, the
 * phone harness, crawlers, a browser marked as the team's (?apr_internal=1),
 * and any moment the address bar still carries a question's words (the app
 * cleans those away as it opens; the pixel reads the address, so it waits).
 *
 * It loads after first paint and must never matter to the app: every call is
 * wrapped, and a blocked script just means fewer counts.
 *
 * Contact us page (feedback.js lane): when it ships, call trackContact() on its
 * success path too. One line, no arguments.
 *
 * No imports on purpose: scripts/check-meta-pixel.js runs this exact file.
 */

export const PIXEL_ID = '1434963498788276';
export const PIXEL_SRC = 'https://connect.facebook.net/en_US/fbevents.js';
/** sessionStorage flag the /q/ landing pages set once their PageView has gone out. */
export const LANDED_KEY = 'apr-px';
const INTERNAL_KEY = 'apr-internal';
const BANK_ID = /^q\d{2}m?$/;
const RETRY_MS = 400;
const RETRIES = 3;
const FALLBACK_MS = 1500;

/** The only events, all sent with no parameters. FirstQuestion is a custom event (trackCustom). */
export const EVENT_LEAD = 'Lead';
export const EVENT_CONTACT = 'Contact';
export const EVENT_FIRST_QUESTION = 'FirstQuestion';
/** sessionStorage flag: this visit's first question has gone to Meta. */
export const FIRST_QUESTION_KEY = 'apr-px-fq';

function store(win, name) {
  try { return win[name] || null; } catch (e) { return null; } // reading it can throw when site data is blocked
}

function safeGet(win, name, key) {
  try { const s = store(win, name); return s ? s.getItem(key) : null; } catch (e) { return null; }
}

function safeRemove(win, name, key) {
  try { const s = store(win, name); if (s) s.removeItem(key); } catch (e) { /* storage blocked */ }
}

function safeSet(win, name, key, value) {
  try { const s = store(win, name); if (s) s.setItem(key, value); } catch (e) { /* storage blocked */ }
}

/**
 * Why the pixel must stay off in this browser, or null when it may run.
 * (The address bar is a separate, temporary check: see urlIsClean.)
 */
export function blockedReason(win) {
  if (!win || !win.document) return 'no-window';
  // Only the public app. The portal is a different bundle, but belt and braces.
  if (typeof __APR_APP__ !== 'undefined' && __APR_APP__ !== 'main') return 'admin';
  const nav = win.navigator || {};
  if (nav.globalPrivacyControl === true || nav.globalPrivacyControl === 'true') return 'gpc';
  if (nav.doNotTrack === '1' || nav.doNotTrack === 'yes' || win.doNotTrack === '1' || nav.msDoNotTrack === '1') return 'dnt';
  const host = (win.location && win.location.hostname) || '';
  const local = host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host === '';
  // The phone harness serves from 127.0.0.1. Only it sets __APR_PIXEL_TEST__, and it
  // does nothing anywhere but a local address, so it cannot widen what runs in production.
  if (local && !win.__APR_PIXEL_TEST__) return 'local';
  if (/bot\b|crawl|spider|slurp/i.test(nav.userAgent || '')) return 'bot';
  if (safeGet(win, 'localStorage', INTERNAL_KEY) === '1') return 'team';
  return null;
}

/**
 * True when nothing in the address carries a person's words. The app turns
 * `?q=<words>` and `#ask=<words>` into a draft and cleans them from the bar as
 * it opens (utils/campaign.js); the pixel sends the address to Meta, so it
 * must see the cleaned one. A bank question ID is not words and may stay.
 */
export function urlIsClean(win) {
  try {
    const loc = win.location;
    if (/^#ask=/i.test(loc.hash || '')) return false;
    const params = new URLSearchParams(loc.search || '');
    const q = params.get('q');
    if (q !== null && !BANK_ID.test(q.trim())) return false;
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * The pixel, for one window (a function so the check can hand it a stand-in).
 * Returns { start, trackLead, trackContact, trackFirstQuestion, isActive }.
 */
export function createMetaPixel(getWindow) {
  let started = false;
  let active = false;
  // A first question asked before the pixel is up (it loads after first paint)
  // waits for it; firstSent also holds where browser storage is blocked.
  let pendingFirst = false;
  let firstSent = false;

  function getWin() {
    try { return getWindow(); } catch (e) { return null; }
  }

  /** The standard Meta loader, plus the settings that must come first. */
  function install(win, skipPageView) {
    if (win.fbq) return; // someone else's pixel is already here: do not double up
    const doc = win.document;
    const fbq = function fbqStub() {
      if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments);
      else fbq.queue.push(arguments);
    };
    win.fbq = fbq;
    if (!win._fbq) win._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    fbq.disablePushState = true; // the app has no routes of its own; no automatic PageViews
    fbq('set', 'autoConfig', false, PIXEL_ID); // BEFORE init: no scraping of buttons or metadata
    // No Limited Data Use, by the owner's call (Oct 15): page views and the two events may
    // build ad audiences (retargeting, lookalikes). Conversations never reach Meta.
    fbq('init', PIXEL_ID); // no advanced matching: init takes the ID and nothing else
    if (!skipPageView) fbq('track', 'PageView');
    if (pendingFirst) {
      pendingFirst = false;
      sendFirstQuestion(win);
    }
    const script = doc.createElement('script');
    script.async = true;
    script.src = PIXEL_SRC;
    (doc.head || doc.documentElement).appendChild(script);
    active = true;
  }

  function attempt(win, tries, skipPageView) {
    if (blockedReason(win)) return;
    if (!urlIsClean(win)) {
      if (tries < RETRIES) win.setTimeout(() => attempt(win, tries + 1, skipPageView), RETRY_MS);
      return;
    }
    install(win, skipPageView);
  }

  /** Call once, as the app opens. Loads the pixel after first paint, or not at all. */
  function start() {
    if (started) return;
    started = true;
    try {
      const win = getWin();
      if (blockedReason(win)) return;
      // The ad landing page already sent this visit's PageView.
      let landed = false;
      if (safeGet(win, 'sessionStorage', LANDED_KEY) === '1') {
        landed = true;
        safeRemove(win, 'sessionStorage', LANDED_KEY);
      }
      let ran = false;
      const go = () => {
        if (ran) return;
        ran = true;
        try { attempt(win, 0, landed); } catch (e) { /* never matters to the app */ }
      };
      if (typeof win.requestAnimationFrame === 'function') win.requestAnimationFrame(() => win.setTimeout(go, 0));
      // A hidden tab never paints: do not wait for it forever.
      win.setTimeout(go, FALLBACK_MS);
    } catch (e) { /* never matters to the app */ }
  }

  /** Sends Lead or Contact, with nothing attached. */
  function track(name) {
    if (!active) return;
    try {
      const win = getWin();
      if (win && typeof win.fbq === 'function') win.fbq('track', name);
    } catch (e) { /* never matters to the app */ }
  }

  /** FirstQuestion, once per visit (this tab's session), with nothing attached. */
  function sendFirstQuestion(win) {
    if (firstSent || safeGet(win, 'sessionStorage', FIRST_QUESTION_KEY) === '1') return;
    firstSent = true;
    safeSet(win, 'sessionStorage', FIRST_QUESTION_KEY, '1');
    win.fbq('trackCustom', EVENT_FIRST_QUESTION);
  }

  /** The visitor asked their own question. Only the visit's first one goes out. */
  function trackFirstQuestion() {
    try {
      const win = getWin();
      if (!win) return;
      if (!active) {
        // Not up yet: it goes out as the pixel loads. Never where the pixel may not run.
        if (!blockedReason(win)) pendingFirst = true;
        return;
      }
      if (typeof win.fbq === 'function') sendFirstQuestion(win);
    } catch (e) { /* never matters to the app */ }
  }

  return {
    start,
    trackLead: () => track(EVENT_LEAD),
    trackContact: () => track(EVENT_CONTACT),
    trackFirstQuestion,
    isActive: () => active,
  };
}

const pixel = createMetaPixel(() => (typeof window === 'undefined' ? null : window));

/** Once, as the app opens (src/index.js). */
export const startMetaPixel = pixel.start;
/** Someone joined the email list (utils/emailList.js, after the server said yes). */
export const trackLead = pixel.trackLead;
/** Someone sent "Ask the real Rob" (AskRobPanel, after the server said yes). */
export const trackContact = pixel.trackContact;
/** The visitor asked their own question (ChatView's send path); only the visit's first goes out. */
export const trackFirstQuestion = pixel.trackFirstQuestion;
